import { IHealthRecord } from "@interfaces";
import { memo, useEffect, useState } from "react";

import { Input, Form, Row, Col, Radio, Button, Space, Select } from "antd";
import { useTranslation } from "react-i18next";

import {
  PATH_HEALTH_RECORDS,
  STEP_FOUR,
  YES,
  communicationDisables,
  concentrationDisables,
  hearingDisables,
  seeingDisables,
  selfCareDisables,
  walkingDisables,
} from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";

import { useNotify } from "@utils";
import { useHistory, useParams } from "react-router";
import { IconCheck, PlusColor, SharedButton, SharedIconButton } from "@components";
import styled from "styled-components";
import { EditOutlined, CloseOutlined } from "@ant-design/icons";
const btnSty = { background: "#00AA67" };
const btnPls = { display: "flex", alignItems: "center", justifyContent: "center" };
interface IProps {
  data: IHealthRecord;
  callback: (data: IHealthRecord, step: number) => void;
}
export const StepThree = memo(({ data, callback }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { success, error } = useNotify();
  const [stepOneData, setStepOneData] = useState<IHealthRecord>({});
  const [thirdForm] = Form.useForm();

  const [see, setSee] = useState<boolean>(false);
  const [hear, setHear] = useState<boolean>(false);
  const [walk, setWalk] = useState<boolean>(false);
  const [concent, setConcent] = useState<boolean>(false);
  const [self, setSelf] = useState<boolean>(false);
  const [communicate, setCommunicate] = useState<boolean>(false);

  const seeing_disability: string = Form.useWatch("seeing_disability", thirdForm);
  const hearing_disability: string = Form.useWatch("hearing_disability", thirdForm);
  const walking_disability: string = Form.useWatch("walking_disability", thirdForm);
  const concentration_disability: string = Form.useWatch("concentration_disability", thirdForm);
  const self_care_disability: string = Form.useWatch("self_care_disability", thirdForm);
  const communication_disability: string = Form.useWatch("communication_disability", thirdForm);
  const disability: string = Form.useWatch("disability", thirdForm);

  useEffect(() => {
    if (data) {
      thirdForm.setFieldsValue({
        ...data,
      });
    }
    setStepOneData(data);
  }, [data]);

  const onFinish = async (values: IHealthRecord) => {
    try {
      if (!id) {
      if (disability === YES) {
          if (
            !seeing_disability ||
            !hearing_disability ||
            !walking_disability ||
            !concentration_disability ||
            !self_care_disability ||
            !communication_disability
          ) {
            throw new Error("Please fill the form");
          }
        }
      }
      await callback(values, STEP_FOUR);
    } catch (err: any) {
      error(t("message.create.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const clearSeeingDisabilityInfo = () => {
    thirdForm.setFieldsValue({
      ...{
        seeing_disability: "",
        seeing_disability_level: "",
      },
    });
  };

  const clearHearingDisabilityInfo = () => {
    thirdForm.setFieldsValue({
      ...{
        hearing_disability: "",
        hearing_disability_level: "",
      },
    });
  };

  const clearWalkingDisabilityInfo = () => {
    thirdForm.setFieldsValue({
      ...{
        walking_disability: "",
        walking_disability_level: "",
      },
    });
  };

  const clearConcentrationDisabilityInfo = () => {
    thirdForm.setFieldsValue({
      ...{
        concentration_disability: "",
        concentration_disability_level: "",
      },
    });
  };

  const clearSelfCareDisabilityInfo = () => {
    thirdForm.setFieldsValue({
      ...{
        self_care_disability: "",
        self_care_disability_level: "",
      },
    });
  };

  const clearCommunicationDisabilityInfo = () => {
    thirdForm.setFieldsValue({
      ...{
        communication_disability: "",
        communication_disability_level: "",
      },
    });
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const goBack = () => history.push(PATH_HEALTH_RECORDS);
  return (
    <Form
      form={thirdForm}
      name="basic"
      initialValues={stepOneData}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      <StyledPageTitle>
        <h4>{t("page.Client_Disability_Data")}</h4>
      </StyledPageTitle>
      <StyledOuterGroupSection>
        <Form.Item
          name="disability"
          label="Do you provide Disability to the client?"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Space>
              <Radio value={"Yes"}>{"Yes"}</Radio>
              <Radio value={"No"}>{"No"}</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      </StyledOuterGroupSection>
      {disability === YES && (
        <>
          <h5>Disability checking sessions</h5>
          <StyledService>
            <p>{"Seeing Disability Check"}</p>
            <div className="action">
              <Button
                style={btnPls}
                type={see ? "primary" : "default"}
                icon={see ? <IconCheck /> : <PlusColor />}
                size={"small"}
                onClick={() => setSee(!see)}
              />
              <SharedIconButton
                style={btnSty}
                type="primary"
                icon={<CloseOutlined />}
                size={"small"}
                onClick={() => clearSeeingDisabilityInfo()}
              />
            </div>
          </StyledService>
          {see && (
            <StyledSection>
              <StyledOuterGroupSection>
                <Form.Item
                  name={"seeing_disability"}
                  label={"Any difficulty in seeing, if the patient’s wearing glasses?"}
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Space>
                      <Radio value={"Yes"}>{"Yes"}</Radio>
                      <Radio value={"No"}>{"No"}</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                {seeing_disability === YES && (
                  <StyledGroupSection>
                    <Form.Item
                      name={"seeing_disability_level"}
                      label={"Hearing Difficulty Level?"}
                      rules={[{ required: true }]}
                    >
                      <Select>
                        {seeingDisables.map((item) => (
                          <Select.Option value={item} key={item}>
                            {item}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </StyledGroupSection>
                )}
              </StyledOuterGroupSection>
            </StyledSection>
          )}
          <StyledService>
            <p>{"Hearing Disability Check"}</p>
            <div className="action">
              <Button
                style={btnPls}
                type={hear ? "primary" : "default"}
                icon={hear ? <IconCheck /> : <PlusColor />}
                size={"small"}
                onClick={() => setHear(!hear)}
              />
              <SharedIconButton
                style={btnSty}
                type="primary"
                icon={<CloseOutlined />}
                size={"small"}
                onClick={() => clearHearingDisabilityInfo()}
              />
            </div>
          </StyledService>
          {hear && (
            <StyledSection>
              <StyledOuterGroupSection>
                <Form.Item
                  name={"hearing_disability"}
                  label={"Any difficulty in hearing, if the patient’s using the Hearing Aid?"}
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Space>
                      <Radio value={"Yes"}>{"Yes"}</Radio>
                      <Radio value={"No"}>{"No"}</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                {hearing_disability === "Yes" && (
                  <StyledGroupSection>
                    <Form.Item
                      name={"hearing_disability_level"}
                      label={"Hearing Difficulty Level?"}
                      rules={[{ required: true }]}
                    >
                      <Select>
                        {hearingDisables.map((item) => (
                          <Select.Option value={item} key={item}>
                            {item}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </StyledGroupSection>
                )}
              </StyledOuterGroupSection>
            </StyledSection>
          )}
          <StyledService>
            <p>{"Walking Disability Check"}</p>
            <div className="action">
              <Button
                style={btnPls}
                type={walk ? "primary" : "default"}
                icon={walk ? <IconCheck /> : <PlusColor />}
                size={"small"}
                onClick={() => setWalk(!walk)}
              />
              <SharedIconButton
                style={btnSty}
                type="primary"
                icon={<CloseOutlined />}
                size={"small"}
                onClick={() => clearWalkingDisabilityInfo()}
              />
            </div>
          </StyledService>
          {walk && (
            <StyledSection>
              <StyledOuterGroupSection>
                <Form.Item
                  name={"walking_disability"}
                  label={"Any difficulty in walking or climbing steps?"}
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Space>
                      <Radio value={"Yes"}>{"Yes"}</Radio>
                      <Radio value={"No"}>{"No"}</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                {walking_disability === YES && (
                  <StyledGroupSection>
                    <Form.Item
                      name={"walking_disability_level"}
                      label={"Walking Difficulty Level?"}
                      rules={[{ required: true }]}
                    >
                      <Select>
                        {walkingDisables.map((item) => (
                          <Select.Option value={item} key={item}>
                            {item}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </StyledGroupSection>
                )}
              </StyledOuterGroupSection>
            </StyledSection>
          )}
          <StyledService>
            <p>{"Concentration Disability Check"}</p>
            <div className="action">
              <Button
                style={btnPls}
                type={concent ? "primary" : "default"}
                icon={concent ? <IconCheck /> : <PlusColor />}
                size={"small"}
                onClick={() => setConcent(!concent)}
              />
              <SharedIconButton
                style={btnSty}
                type="primary"
                icon={<CloseOutlined />}
                size={"small"}
                onClick={() => clearConcentrationDisabilityInfo()}
              />
            </div>
          </StyledService>
          {concent && (
            <StyledSection>
              <StyledOuterGroupSection>
                <Form.Item
                  name={"concentration_disability"}
                  label={"Any difficulty in remembering or concentrating?"}
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Space>
                      <Radio value={"Yes"}>{"Yes"}</Radio>
                      <Radio value={"No"}>{"No"}</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                {concentration_disability === YES && (
                  <StyledGroupSection>
                    <Form.Item
                      name={"concentration_disability_level"}
                      label={"Concentration Difficulty Level?"}
                      rules={[{ required: true }]}
                    >
                      <Select>
                        {concentrationDisables.map((item) => (
                          <Select.Option value={item} key={item}>
                            {item}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </StyledGroupSection>
                )}
              </StyledOuterGroupSection>
            </StyledSection>
          )}
          {/*  */}
          <StyledService>
            <p>{"Self-Care Disability Check"}</p>
            <div className="action">
              <Button
                style={btnPls}
                type={self ? "primary" : "default"}
                icon={self ? <IconCheck /> : <PlusColor />}
                size={"small"}
                onClick={() => setSelf(!self)}
              />
              <SharedIconButton
                style={btnSty}
                type="primary"
                icon={<CloseOutlined />}
                size={"small"}
                onClick={() => clearSelfCareDisabilityInfo()}
              />
            </div>
          </StyledService>
          {self && (
            <StyledSection>
              <StyledOuterGroupSection>
                <Form.Item
                  name={"self_care_disability"}
                  label={"Any difficulty in self-care (washing all-over or wearing)?"}
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Space>
                      <Radio value={"Yes"}>{"Yes"}</Radio>
                      <Radio value={"No"}>{"No"}</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                {self_care_disability === YES && (
                  <StyledGroupSection>
                    <Form.Item
                      name={"self_care_disability_level"}
                      label={"Self-Care Difficulty Level?"}
                      rules={[{ required: true }]}
                    >
                      <Select>
                        {selfCareDisables.map((item) => (
                          <Select.Option value={item} key={item}>
                            {item}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </StyledGroupSection>
                )}
              </StyledOuterGroupSection>
            </StyledSection>
          )}
          {/*  */}
          <StyledService>
            <p>{"Communication Disability Check"}</p>
            <div className="action">
              <Button
                style={btnPls}
                type={communicate ? "primary" : "default"}
                icon={communicate ? <IconCheck /> : <PlusColor />}
                size={"small"}
                onClick={() => setCommunicate(!communicate)}
              />
              <SharedIconButton
                style={btnSty}
                type="primary"
                icon={<CloseOutlined />}
                size={"small"}
                onClick={() => clearCommunicationDisabilityInfo()}
              />
            </div>
          </StyledService>
          {communicate && (
            <StyledSection>
              <StyledOuterGroupSection>
                <Form.Item
                  name={"communication_disability"}
                  label={"Any difficulty in communication with each other?"}
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Space>
                      <Radio value={"Yes"}>{"Yes"}</Radio>
                      <Radio value={"No"}>{"No"}</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                {communication_disability === YES && (
                  <StyledGroupSection>
                    <Form.Item
                      name={"communication_disability_level"}
                      label={"Communication Difficulty Level?"}
                      rules={[{ required: true }]}
                    >
                      <Select>
                        {communicationDisables.map((item) => (
                          <Select.Option value={item} key={item}>
                            {item}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </StyledGroupSection>
                )}
              </StyledOuterGroupSection>
            </StyledSection>
          )}
        </>
      )}
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}></Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <StyledFormAction>
            <SharedButton type="default" htmlType="button" size="middle" onClick={goBack}>
              {t("page.cancel")}
            </SharedButton>
            <SharedButton type="primary" htmlType="submit" size="middle">
              {t("page.save")}
            </SharedButton>
          </StyledFormAction>
        </Col>
      </Row>
    </Form>
  );
});

const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
  margin: 20px 0;
`;
const StyledService = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: 1.5px solid #f06d24;
  border-radius: 5px;
  padding: 10px;
  margin: 16px 0;
  // cursor: pointer;
  .action {
    display: flex;
    column-gap: 10px;
    align-items: center;
  }
`;
const StyledSection = styled.section`
  margin-top: 16px;
  padding: 30px 32px;
  background: #fff;
  border-radius: 5px;
  transition: 0.3s;
  .ant-space {
    margin: 14px 0;
    width: 100%;
  }
  .ant-radio-group {
    width: 100%;
  }
`;
const StyledGroupSection = styled.section`
  padding: 30px;
  border: 1px solid #c1c1c1;
  background-color: #f6f6f6;
  border-radius: 6px;
  .titale-optoin {
    margin-bottom: 16px;
  }

  p {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    color: #222222;
  }
`;
const StyledOuterGroupSection = styled.section`
  margin: 14px 0;
  p {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    color: #222222;
  }
`;
const StyledPageTitle = styled.header`
  margin-bottom: 40px;
  h4 {
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
  }
`;
